/**
 * Facturas x Mayor — pantalla unificada de facturación al por mayor.
 *
 * La vista principal es el mantenimiento de facturas embebido (ares9720:
 * listado por fechas/cliente, anular facturas sin pagos e imprimir).
 * Tarjetas de acción:
 *   - "Facturar x mayor" (ares9710): factura vehículos al contado o a
 *     crédito, en diálogo a pantalla completa; al guardar, se recarga.
 *   - "Modificar precio motor" (ares9756): ajusta precios de chasis de una
 *     factura y recalcula cuotas (diálogo con barra Volver).
 *   - "Chasis vendidos" (ares9755): chasis facturados con su estado de
 *     saldo y la acción de saldar (diálogo con barra Volver).
 *
 * Si el usuario dejó una factura a medio llenar (borrador "xmayor", ver
 * lib/borradorventa), un aviso arriba permite retomarla o descartarla.
 *
 * Todo lo consignado (conduces de entrega, marcado de lo vendido y las
 * facturas que salen de ahí) vive en su propio segmento del módulo:
 * xmayorconsignaciones.js.
 *
 * Sustituye las entradas de menú de facturarxmayor.js,
 * mantenimientofacturasxmayor.js, modificarpreciomotor.js y
 * chasisvendidosxmayor.js.
 */
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import TwoWheelerOutlinedIcon from "@mui/icons-material/TwoWheelerOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import FacturarXmayor from "modules/ventasxmayor/facturarxmayor";
import MantenimientoFacturasXmayor from "modules/ventasxmayor/mantenimientofacturasxmayor";
import ModificarPrecioMotor from "modules/ventasxmayor/modificarpreciomotor";
import ChasisVendidosXmayor from "modules/ventasxmayor/chasisvendidosxmayor";
import useAccionInicial from "lib/useAccionInicial";
import { leerBorrador, borrarBorrador } from "lib/borradorventa";
import AvisoBorradorVenta from "components/borradorventa/AvisoBorradorVenta";

export default function XmayorFacturas() {
  const [controller] = useMaterialUIController();
  const { roleDetalles, usuarioLog } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeFacturar = tienePermisosModulo(roleDetalles, "ares9710");
  const puedeMantenimiento = tienePermisosModulo(roleDetalles, "ares9720");
  const puedePrecios = tienePermisosModulo(roleDetalles, "ares9756");
  const puedeChasis = tienePermisosModulo(roleDetalles, "ares9755");

  const [facturarOpen, setFacturarOpen] = useState(false);
  const [preciosOpen, setPreciosOpen] = useState(false);
  const [chasisOpen, setChasisOpen] = useState(false);
  // "Continuar" desde el aviso: el formulario abre con el borrador ya restaurado.
  const [restaurarFactura, setRestaurarFactura] = useState(false);
  const abrirFacturar = (restaurar = false) => {
    setRestaurarFactura(restaurar);
    setFacturarOpen(true);
  };

  // Factura sin terminar de este usuario en este navegador; se relee al
  // cerrar el formulario.
  const [borradorFactura, setBorradorFactura] = useState(null);
  useEffect(() => {
    if (facturarOpen) return;
    setBorradorFactura(puedeFacturar ? leerBorrador("xmayor", usuarioLog) : null);
  }, [facturarOpen, usuarioLog, puedeFacturar]);
  const descartarBorrador = () => {
    borrarBorrador("xmayor", usuarioLog);
    setBorradorFactura(null);
  };

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    facturar: { permitido: puedeFacturar, abrir: () => abrirFacturar() },
    precios: { permitido: puedePrecios, abrir: () => setPreciosOpen(true) },
    chasis: { permitido: puedeChasis, abrir: () => setChasisOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras facturar o modificar).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Facturas x Mayor"
        subtitle="Factura, consulta y anula ventas al por mayor en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Factura en pausa (borrador guardado en este navegador) */}
        <AvisoBorradorVenta
          borrador={borradorFactura}
          etiqueta="factura x mayor"
          onContinuar={() => abrirFacturar(true)}
          onDescartar={descartarBorrador}
          sx={{ mb: 2 }}
        />
        {(puedeFacturar || puedePrecios || puedeChasis) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeFacturar && (
              <Grid item xs={12} md={4}>
                <ActionCard
                  icon={ReceiptLongOutlinedIcon}
                  titulo="Facturar x mayor"
                  descripcion="Factura vehículos al contado o a crédito con cuotas"
                  color={FP.color.success}
                  onClick={() => abrirFacturar()}
                />
              </Grid>
            )}
            {puedePrecios && (
              <Grid item xs={12} md={4}>
                <ActionCard
                  icon={PriceChangeOutlinedIcon}
                  titulo="Modificar precio motor"
                  descripcion="Ajusta precios de chasis de una factura y recalcula cuotas"
                  color={FP.color.accentBlue}
                  onClick={() => setPreciosOpen(true)}
                />
              </Grid>
            )}
            {puedeChasis && (
              <Grid item xs={12} md={4}>
                <ActionCard
                  icon={TwoWheelerOutlinedIcon}
                  titulo="Chasis vendidos"
                  descripcion="Chasis facturados con estado de saldo y acción de saldar"
                  color={FP.color.info}
                  onClick={() => setChasisOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: mantenimiento de facturas (anular/imprimir) */}
        {puedeMantenimiento && <MantenimientoFacturasXmayor embedded refreshToken={refreshKey} />}
      </Box>

      {/* Facturar x mayor (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={facturarOpen} onClose={() => setFacturarOpen(false)}>
        <FacturarXmayor
          embedded
          restaurarBorrador={restaurarFactura}
          onExit={() => {
            setFacturarOpen(false);
            refrescar();
          }}
          onSaved={refrescar}
        />
      </Dialog>

      {/* Modificar precio motor (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={preciosOpen} onClose={() => setPreciosOpen(false)}>
        <BarraVolver
          onExit={() => {
            setPreciosOpen(false);
            refrescar();
          }}
        />
        <ModificarPrecioMotor />
      </Dialog>

      {/* Chasis vendidos (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={chasisOpen} onClose={() => setChasisOpen(false)}>
        <BarraVolver onExit={() => setChasisOpen(false)} />
        <ChasisVendidosXmayor />
      </Dialog>
    </Box>
  );
}
